import type { WindowState } from '../core/types.js';
import { localDate, minutesSinceLocalMidnight } from '../core/time.js';

export function applyUsage(state: WindowState, useAt: Date = new Date()): { state: WindowState; explanation: string; outcome: 'success' | 'near_miss' | 'early_slip' } {
  const dayStr = localDate(useAt);
  const minutes = minutesSinceLocalMidnight(useAt);
  // Only the FIRST use of the day matters for first-use window plans.
  const isFirstToday = state.lastDayClosed !== dayStr && state.todayFirstUseMinutes === undefined;
  const target = state.targetFirstUseMinutes;
  const nearWindow = 10;
  let outcome: 'success' | 'near_miss' | 'early_slip';
  if (!isFirstToday) {
    return { state, explanation: 'Logged.', outcome: 'success' };
  }
  if (minutes >= target) outcome = 'success';
  else if (minutes >= target - nearWindow) outcome = 'near_miss';
  else outcome = 'early_slip';
  const next: WindowState = { ...state, todayFirstUseMinutes: minutes };
  const explanation =
    outcome === 'success' ? `You held the line until target.` :
    outcome === 'near_miss' ? `Close to the target — small miss.` :
    `Used before target. Wean will hold or ease the next.`;
  return { state: { ...next, lastExplanation: explanation }, explanation, outcome };
}

export function closeDayIfNeeded(state: WindowState, today: Date = new Date()): { state: WindowState; closed: boolean; outcome?: 'use_free' | 'success' | 'near_miss' | 'early_slip' } {
  const dayStr = localDate(today);
  if (state.lastDayClosed === dayStr) return { state, closed: false };
  // Only roll once per day on app open
  const dow = today.getDay();
  const isActive = state.activeDays.includes(dow);
  let outcome: 'use_free' | 'success' | 'near_miss' | 'early_slip' = 'use_free';
  if (!isActive) {
    return { state: { ...state, lastDayClosed: dayStr, todayFirstUseMinutes: undefined }, closed: true };
  }
  const tfu = state.todayFirstUseMinutes;
  if (tfu === undefined) outcome = 'use_free';
  else if (tfu >= state.targetFirstUseMinutes) outcome = 'success';
  else if (tfu >= state.targetFirstUseMinutes - 10) outcome = 'near_miss';
  else outcome = 'early_slip';
  const successDays = (outcome === 'success' || outcome === 'use_free') ? state.successDays + 1 : state.successDays;
  const missDays = outcome === 'early_slip' ? state.missDays + 1 : state.missDays;
  // Adjust after enough successes / misses (rolling)
  let target = state.targetFirstUseMinutes;
  if (successDays > 0 && successDays % 4 === 0) target = Math.min(target + state.shiftStepMinutes, 23 * 60 + 59);
  if (missDays > 0 && missDays % 3 === 0) target = Math.max(target - Math.floor(state.shiftStepMinutes / 2), 0);
  return {
    state: { ...state, targetFirstUseMinutes: target, successDays, missDays, lastDayClosed: dayStr, todayFirstUseMinutes: undefined },
    closed: true, outcome,
  };
}
